/**
 * The winged sheep over the About page title, drawn and animated in code so its wings bend and
 * beat like a bird's and its legs swing, instead of moving as stiff cut-outs.
 *
 * The sheep is drawn in its own coordinates: facing +x, y down, origin on its back just behind
 * the shoulders, about 60 units from rump to nose. Its wings are rigged in 3D like the hero
 * dragon's (an arm and a hand that bend at the wrist, beating round the body's long axis) and
 * projected for a camera a little above, so a wing reads as thin when level and full when raised.
 *
 * Most of the time it hovers over its cloud on a lazy wingbeat. Every so often it beats hard,
 * climbs a little and floats back down with its wings held up and its legs reaching for the
 * cloud, which gives a little under the downwash when the sheep comes close.
 */

export interface SheepPose {
  cloud: string;
  shadow: string;
  shadowOpacity: string;
  sheep: string;
  farWing: string;
  farWingLines: string;
  farLegs: string;
  nearLegs: string;
  wool: string;
  head: string;
  eye: string;
  nearWing: string;
  nearWingLines: string;
}

export interface Circle { cx: number; cy: number; r: number; }

interface Pt { x: number; y: number; }

/* —— The scene, in viewBox units (140 × 100) —— */
export const CLOUD = 'M42,98 C32,98 32,88 42,88 C44,81 55,79 61,84 C65,77 79,76 83,83 C90,80 99,83 99,90 C107,90 108,98 100,98 Z';
/** The middle of the cloud's flat base: it squashes down toward it. */
const CLOUD_BASE: Pt = { x: 70, y: 98 };
/** Roughly where the cloud's top is under the sheep's feet. */
export const CLOUD_TOP = 80;
/** Where the sheep's origin hovers. */
const HOME: Pt = { x: 63, y: 42 };

/* —— The sheep's fixed parts, in its own coordinates —— */
/** Puffs of wool round the body's edge, then a few to fill the middle. */
export const WOOL: Circle[] = [
  { cx: -17, cy: 6, r: 7 }, { cx: -8, cy: 1, r: 8 }, { cx: 3, cy: 0, r: 8 }, { cx: 13, cy: 3, r: 7.5 },
  { cx: 19, cy: 10, r: 6.5 }, { cx: 16, cy: 18, r: 6.5 }, { cx: 6, cy: 21, r: 7 }, { cx: -5, cy: 21, r: 7 },
  { cx: -15, cy: 18, r: 7 }, { cx: -21, cy: 12, r: 6.5 },
  { cx: -8, cy: 11, r: 9 }, { cx: 0, cy: 11, r: 11 }, { cx: 8, cy: 11, r: 9 }
];
/** The body squashes and stretches about this height. */
const WOOL_MIDDLE = 11;
/** A curl of wool over the near wing's root, so the wing grows out of the fleece: a fill that
 *  runs down into the body, and an outline along its top edge only. */
export const SHOULDER_TUFT = 'M-9,2 C-10,-3 -5,-6.5 -2,-4.5 C0,-7.5 5,-7.5 6,-3.5 C8.5,-3.5 9.5,0 8,2.5 Z';
export const SHOULDER_TUFT_EDGE = 'M-8.6,-.6 C-8.4,-4 -4.6,-6 -2,-4.5 C0,-7.5 5,-7.5 6,-3.5 C8,-3.5 9,-1.4 8.6,.4';
/** Where the head sits on the body; the head is drawn round its own origin, the top of the neck. */
const NECK: Pt = { x: 21, y: 5 };
/** A tuft of wool on top of the head, in head coordinates. */
export const FORELOCK: Circle[] = [{ cx: 1, cy: -3.6, r: 3.4 }, { cx: 4.8, cy: -5, r: 3.1 }, { cx: 8.2, cy: -3.4, r: 2.5 }];
export const EYE: Pt = { x: 9.4, y: 2.2 };
const FEET = 33;

/* —— Wingbeat —— */
const HOVER_PERIOD = .72;
const HOP_PERIOD = .38;
/** Share of a beat spent on the (powerful) downstroke. */
const DOWNSTROKE = .44;
const HOVER_MID = rad(32);
const HOVER_AMPLITUDE = rad(38);
const HOP_MID = rad(30);
const HOP_AMPLITUDE = rad(52);
const WRIST_LAG = rad(24);
/** Wings held up in a V while it floats down. */
const FLOAT_ARM = rad(68);
const FLOAT_HAND = rad(12);
/** How far the body rises and falls with each beat. */
const HOVER_BOB = 1.5;
const HOP_BOB = 2.2;

/* —— Wing planform: x along the body from the shoulder, s out along the span —— */
const SHOULDER: Pt = { x: 1, y: -5 };
/** The far wing's root, a little higher and further forward as seen from slightly above. */
const FAR_SHOULDER: Pt = { x: 3, y: -6.5 };
const WRIST = { x: 6, s: 11 };
/** Feather tips from the wingtip in toward the body. */
const FEATHERS = [
  { x: 1, s: 32 }, { x: -4.5, s: 29.5 }, { x: -9.5, s: 25.5 }, { x: -12.5, s: 20 }, { x: -14, s: 13.5 }, { x: -13, s: 7 }
];
const WING_ROOT_BACK = { x: -9, s: 0 };
/** How much of the span toward the camera shows as downward on screen (the camera is a little above). */
const VIEW_TILT = .3;

/* —— Legs —— */
const LEGS = [
  { hip: { x: 12, y: 21 }, lean: 7, lag: 0, far: false },
  { hip: { x: -11, y: 21 }, lean: -5, lag: .9, far: false },
  { hip: { x: 15, y: 20 }, lean: 4, lag: 1.7, far: true },
  { hip: { x: -8, y: 20 }, lean: -8, lag: 2.6, far: true }
];
const THIGH = 6.5;
const SHIN = 6;

/* —— Hops and blinks —— */
const FIRST_HOP_AT = 3.5;
const HOP_PAUSES = [8, 11, 9.5];
const HOP_LENGTH = 3;
const HOP_HEIGHT = 10;
/** Seconds between blinks; the short one makes a double blink. */
const BLINKS = [3.4, 5.1, .32, 4.6];
const BLINK_LENGTH = .16;

interface State {
  time: number;
  /** Point (0–1) of the current wingbeat. */
  beat: number;
  /** 0 for the lazy hover, 1 for the hard beats of a hop. */
  vigour: number;
  /** 1 while it floats down on raised wings. */
  float: number;
  /** How far it has risen above its hover, in viewBox units (negative is up). */
  lift: number;
  /** 1 with the eye open, 0 shut. */
  open: number;
}

export class SheepHover {

  private time = 0;
  private beat = 0;
  /** Seconds into the current hop; negative while waiting for the next one. */
  private hop = -FIRST_HOP_AT;
  private hops = 0;
  private sinceBlink = 0;
  private blinks = 0;

  /** A still sheep, wings raised, for the server render and for reduced motion. */
  static restingPose(): SheepPose {
    return pose({ time: 0, beat: .86, vigour: 0, float: 0, lift: 0, open: 1 });
  }

  /** Moves the hover on by `dt` seconds. */
  advance(dt: number): SheepPose {
    this.time += dt;

    this.hop += dt;
    if (this.hop >= HOP_LENGTH) {
      this.hop = -HOP_PAUSES[this.hops++ % HOP_PAUSES.length];
    }
    const h = this.hop < 0 ? -1 : this.hop;
    // Hard beats for the first second, then it floats down with its wings up and settles on a few
    // lazy beats, dipping a touch below its hover before it finds it again.
    const vigour = smoothstep(0, .15, h) * (1 - smoothstep(.85, 1.05, h));
    const float = smoothstep(.85, 1.05, h) * (1 - smoothstep(2, 2.5, h));
    const climb = smoothstep(.05, 1, h) * (1 - smoothstep(1, 2.5, h));
    const dip = h > 2.2 && h < 3 ? Math.sin(Math.PI * (h - 2.2) / .8) : 0;
    const lift = -HOP_HEIGHT * climb + 1.5 * dip;

    this.beat = (this.beat + dt / lerp(HOVER_PERIOD, HOP_PERIOD, vigour)) % 1;

    this.sinceBlink += dt;
    if (this.sinceBlink >= BLINKS[this.blinks % BLINKS.length]) {
      this.sinceBlink = 0;
      this.blinks++;
    }
    const open = this.blinks > 0 && this.sinceBlink < BLINK_LENGTH
      ? 1 - Math.sin(Math.PI * this.sinceBlink / BLINK_LENGTH)
      : 1;

    return pose({ time: this.time, beat: this.beat, vigour, float, lift, open });
  }
}

function pose({ time, beat, vigour, float, lift, open }: State): SheepPose {
  // Stroke angle: 0 with the wings at the top, π at the bottom. The downstroke is the quicker half.
  const stroke = beat < DOWNSTROKE
    ? beat / DOWNSTROKE * Math.PI
    : Math.PI + (beat - DOWNSTROKE) / (1 - DOWNSTROKE) * Math.PI;
  const flapping = 1 - float;

  // Raised wings tremble a little as they hold the air.
  const tremble = rad(2.5) * float * Math.sin(time * 31);
  const beatArm = lerp(HOVER_MID, HOP_MID, vigour) + lerp(HOVER_AMPLITUDE, HOP_AMPLITUDE, vigour) * Math.cos(stroke);
  const arm = lerp(FLOAT_ARM + tremble, beatArm, flapping);
  // The hand trails the arm: still up when the arm comes down, still down when it goes up.
  const hand = lerp(FLOAT_ARM + FLOAT_HAND + tremble, arm + WRIST_LAG * Math.sin(stroke), flapping);
  // It folds its hands in a little on the way up.
  const reach = 1 - .3 * flapping * Math.max(0, -Math.sin(stroke));

  // Each downstroke lifts the body; the head is held steadier than the body, like a bird's.
  const bob = lerp(HOVER_BOB, HOP_BOB, vigour) * flapping * Math.cos(stroke);
  const at = {
    x: HOME.x + 1.6 * Math.sin(time * 2 * Math.PI / 7.3),
    y: HOME.y + bob + lift + .8 * Math.sin(time * 2 * Math.PI / 5.1)
  };
  // Nose up as it climbs, a little down as it floats, looking for the cloud.
  const pitch = -7 * vigour + 3 * float + 1.5 * Math.sin(time * .8);
  const nod = 2 * flapping * Math.sin(stroke) + 6 * float;
  const squash = .025 * flapping * Math.cos(stroke);

  // The cloud gives under the sheep when it comes close, and a little with each downstroke.
  const gap = CLOUD_TOP - (at.y + FEET);
  const downwash = flapping * Math.max(0, Math.sin(stroke));
  const give = .07 * (1 - smoothstep(.5, 4, gap)) + .03 * downwash * (1 - smoothstep(2, 14, gap));
  const height = Math.max(0, gap);

  const far = wing(arm, hand, reach, FAR_SHOULDER, -1);
  const near = wing(arm, hand, reach, SHOULDER, 1);
  const legs = (isFar: boolean) => LEGS
    .filter(l => l.far === isFar)
    .map(l => leg(l.hip, l.lean, l.lag, beat, vigour, float))
    .join(' ');

  return {
    cloud: about(CLOUD_BASE, `scale(${f(1 + give * .6)} ${f(1 - give)})`),
    shadow: `translate(${f(at.x + 2)} 0) scale(${f(1 - height * .02)} 1)`,
    shadowOpacity: f(.04 + .14 * (1 - smoothstep(0, 16, height))),
    sheep: `translate(${f(at.x)} ${f(at.y)}) rotate(${f(pitch)})`,
    farWing: far.outline,
    farWingLines: far.lines,
    farLegs: legs(true),
    nearLegs: legs(false),
    wool: about({ x: 0, y: WOOL_MIDDLE }, `scale(${f(1 + squash * .5)} ${f(1 - squash)})`),
    head: `translate(${f(NECK.x)} ${f(NECK.y - bob * .6)}) rotate(${f(-pitch * .6 + nod)})`,
    eye: about(EYE, `scale(1 ${f(Math.max(.1, open))})`),
    nearWing: near.outline,
    nearWingLines: near.lines
  };
}

function wing(arm: number, hand: number, reach: number, shoulder: Pt, side: 1 | -1): { outline: string; lines: string } {
  // A point on the arm at span s, or on the hand at planform (x, s), projected to the screen.
  const project = (x: number, up: number, out: number): Pt =>
    ({ x: shoulder.x + x, y: shoulder.y - up + side * out * VIEW_TILT });
  const onArm = (x: number, s: number) => project(x, s * Math.sin(arm), s * Math.cos(arm));
  const wristUp = WRIST.s * Math.sin(arm);
  const wristOut = WRIST.s * Math.cos(arm);
  const onHand = (x: number, s: number) => {
    const along = (s - WRIST.s) * reach;
    return project(WRIST.x + (x - WRIST.x) * (.6 + .4 * reach),
      wristUp + along * Math.sin(hand), wristOut + along * Math.cos(hand));
  };
  const at = (x: number, s: number) => s <= WRIST.s ? onArm(x, s) : onHand(x, s);

  const root = onArm(3, 0);
  const elbow = onArm(6.8, 5.5);
  const wrist = onArm(WRIST.x, WRIST.s);
  const knuckle = onHand(5.5, 23);
  const tips = FEATHERS.map(t => at(t.x, t.s));
  const back = onArm(WING_ROOT_BACK.x, WING_ROOT_BACK.s);
  const middle = at(-3.5, 13);

  // Each feather is a rounded lobe through its tip, pushed a little out from the middle of the
  // wing, with a notch toward the middle between it and the next.
  const lobe = (tip: Pt) => toward(tip, middle, -.14);
  const notches = tips.map((tip, i) => i < tips.length - 1 ? toward(mid(tip, tips[i + 1]), middle, .2) : back);

  let outline = `M${p(root)} Q${p(elbow)} ${p(wrist)} Q${p(knuckle)} ${p(toward(tips[0], knuckle, .3))}`;
  tips.forEach((tip, i) => outline += ` Q${p(lobe(tip))} ${p(notches[i])}`);
  outline += ' Z';

  // The edge of the coverts, and a short line in from each notch between the long feathers.
  let lines = `M${p(at(-10, 2))} Q${p(at(-4, 12))} ${p(at(1.5, 21))}`;
  for (const notch of notches.slice(0, -1)) {
    lines += ` M${p(notch)} L${p(toward(notch, wrist, .35))}`;
  }
  return { outline, lines };
}

function leg(hip: Pt, lean: number, lag: number, beat: number, vigour: number, float: number): string {
  // Legs dangle and swing a beat behind the wings; on a climb they trail back with the knees
  // bent, and as it floats down they reach straight for the cloud.
  const swing = (4 + 4 * vigour) * Math.sin(2 * Math.PI * beat - lag);
  const upper = rad(lean * (1 - .5 * float) + swing - 30 * vigour);
  const lower = upper - rad(10 + 50 * vigour - 8 * float);
  const knee = { x: hip.x + Math.sin(upper) * THIGH, y: hip.y + Math.cos(upper) * THIGH };
  const foot = { x: knee.x + Math.sin(lower) * SHIN, y: knee.y + Math.cos(lower) * SHIN };
  return `M${p(hip)} L${p(knee)} L${p(foot)}`;
}

/** A transform applied about `pt` instead of the origin. */
function about(pt: Pt, transform: string): string {
  return `translate(${f(pt.x)} ${f(pt.y)}) ${transform} translate(${f(-pt.x)} ${f(-pt.y)})`;
}

function toward(a: Pt, b: Pt, t: number): Pt {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) };
}

function mid(a: Pt, b: Pt): Pt {
  return toward(a, b, .5);
}

function smoothstep(from: number, to: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - from) / (to - from)));
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function rad(degrees: number): number {
  return degrees * Math.PI / 180;
}

function f(n: number): string {
  return n.toFixed(2);
}

function p(pt: Pt): string {
  return `${pt.x.toFixed(2)},${pt.y.toFixed(2)}`;
}
